import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
const SLIDER_SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
];
/**
 * tiktok-videos — Shrine "TikTok videos" at CONFIG-BY-CONFIG parity (shrine-pro-ref/sections/tiktok-videos.liquid).
 * A heading (+ the shared word-HIGHLIGHT engine and an optional pattern background) over a responsive
 * grid/slider of TikTok video embeds. Each slide is an inline `column` child block (Shrine block type
 * `column`) whose single setting is a raw TikTok embed code; when empty, the exact Shrine `image`
 * placeholder_svg renders in its place.
 *
 * Field-type mapping (Shopify → ours): inline_richtext title → richtext heading (bold/italic markup drives
 * the two highlight engines), the two `title_highlight_*` clusters → the shared headingHighlight engine, the
 * `pattern_bg` cluster → the shared pattern engine, the six `color_scheme` selects → our universal
 * `colorScheme` + slider-scheme tokens, `html` → `textarea` (raw embed code, sanitized on render), header →
 * group, visible_if → visibleWhen. Slider defaults are faithful to Shrine (slider_desktop/mobile default ON,
 * 3 columns, mobile-=×0.75 padding).
 *
 * The Shopify-platform-only knobs are DEFERRED with justification (see Component.tsx): `display_id`
 * (section-merge helper), the `custom_image_background` pair (Shopify img_url pipeline), the live Splide
 * autoplay/loop carousel JS (needs a client island — the controls + native scroll-snap render, and the
 * TikTok embeds themselves hydrate via the React-free tiktokEmbedInit engine).
 */
export const tiktokVideosSchema = {
    type: 'tiktok-videos',
    name: 'TikTok videos',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── General (Shrine HEADER: General) ─────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            group: 'General',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'desktop-hidden', label: 'Mobile only' },
                { value: 'mobile-hidden', label: 'Desktop only' },
            ],
        },
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: 'General',
            default: { format: 'html', html: '<p>TikTok videos</p>' },
            info: 'Bold a word to apply highlight 1, italic to apply highlight 2.',
        },
        ...headingHighlightFields(1, 'General').map((f) => f.id === 'headingHighlight1'
            ? { ...f, info: 'Bold a word in the heading to style it with this.' }
            : f),
        ...headingHighlightFields(2, 'General').map((f) => f.id === 'headingHighlight2'
            ? { ...f, info: 'Italicize a word in the heading to style it with this.' }
            : f),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: 'General',
        },
        // ── Background pattern (shared engine — Shrine pattern_bg cluster). ────────
        ...patternFields('General'),
        // ── Slider (Shrine HEADER: Slider) ───────────────────────────────────────
        {
            id: 'sliderType',
            type: 'select',
            label: 'Type',
            group: 'Slider',
            default: 'slide',
            info: 'Classic (Slide) or infinite (Loop). The looping/autoplay carousel needs a client island.',
            options: [
                { value: 'slide', label: 'Classic' },
                { value: 'loop', label: 'Infinite' },
            ],
        },
        {
            id: 'autoplay',
            type: 'checkbox',
            label: 'Enable autoplay',
            group: 'Slider',
            default: false,
        },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Autoplay speed',
            group: 'Slider',
            min: 1,
            max: 15,
            step: 0.5,
            unit: 's',
            default: 5,
            visibleWhen: { field: 'autoplay', equals: true },
        },
        {
            id: 'arrowsScheme',
            type: 'select',
            label: 'Arrows color scheme',
            group: 'Slider',
            default: 'inverse',
            options: SLIDER_SCHEME_OPTIONS,
        },
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: 'Slider',
            default: true,
        },
        {
            id: 'dotsScheme',
            type: 'select',
            label: 'Dots color scheme',
            group: 'Slider',
            default: 'inverse',
            options: SLIDER_SCHEME_OPTIONS,
        },
        // ── Desktop (Shrine HEADER: Desktop) ─────────────────────────────────────
        {
            id: 'desktopFullPage',
            type: 'checkbox',
            label: 'Full page width',
            group: 'Desktop',
            default: false,
        },
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Slides per page',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 3,
        },
        {
            id: 'sliderDesktop',
            type: 'checkbox',
            label: 'Enable desktop slider',
            group: 'Desktop',
            default: true,
            info: 'The settings below are applied if this option is checked. Slides become a slider only when there are more slides than columns.',
        },
        {
            id: 'perMoveDesktop',
            type: 'range',
            label: 'Slides to scroll on one move',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing',
            group: 'Desktop',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 24,
        },
        {
            id: 'desktopSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide',
            group: 'Desktop',
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopPaddingCalc',
            type: 'checkbox',
            label: 'Disable empty preview on first & last slide',
            group: 'Desktop',
            default: true,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Desktop',
            default: false,
            info: 'Only available if Slides per page is set to 1.',
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopDots',
            type: 'select',
            label: 'Dots position',
            group: 'Desktop',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        {
            id: 'desktopArrows',
            type: 'select',
            label: 'Arrows position',
            group: 'Desktop',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        // ── Mobile (Shrine HEADER: Mobile) ───────────────────────────────────────
        {
            id: 'sliderMobile',
            type: 'checkbox',
            label: 'Enable mobile slider',
            group: 'Mobile',
            default: true,
            info: 'The settings below are applied if this option is checked.',
        },
        {
            id: 'mobilePreview',
            type: 'checkbox',
            label: 'Preview of prev & next slides',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileDots',
            type: 'select',
            label: 'Dots position',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        {
            id: 'mobileArrows',
            type: 'select',
            label: 'Arrows position',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        // ── Section (universal: color scheme, padding_top/bottom, width) ─────────
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    // The TikTok video inline block (Shrine `column`). UX improvement over Shrine's raw-embed-code field
    // (founder 2026-07-05, mirroring our own Video section): the seller pastes just the video LINK and we
    // generate the embed for them; a full TikTok embed code still works too (power-user / Shrine parity).
    blocks: [
        {
            type: 'column',
            name: 'TikTok video',
            max: 20,
            fields: [
                {
                    id: 'videoUrl',
                    aiWritable: false, // 0059 — machine format, not prose
                    type: 'text',
                    label: 'TikTok video link',
                    group: 'TikTok video',
                    maxLength: 5000,
                    info: 'Paste the video link (e.g. https://www.tiktok.com/@user/video/123…) — the embed is generated for you. A full TikTok embed code also works.',
                },
            ],
        },
    ],
    maxBlocks: 20,
};
export const tiktokVideosPresets = [
    {
        name: 'TikTok videos',
        settings: {
            visibility: 'always-display',
            heading: { format: 'html', html: '<p>TikTok videos</p>' },
            headingSize: 'h1',
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            headingHighlight2: 'none',
            columnsDesktop: 3,
            desktopSpacing: 24,
            sliderType: 'slide',
            autoplay: false,
            autoplaySpeed: 5,
            arrowsScheme: 'inverse',
            transparentArrows: true,
            dotsScheme: 'inverse',
            desktopFullPage: false,
            sliderDesktop: true,
            perMoveDesktop: 1,
            desktopSidePadding: 0,
            desktopPaddingCalc: true,
            desktopArrows: 'sides',
            desktopDots: 'under',
            sliderMobile: true,
            mobilePreview: false,
            mobileArrows: 'under',
            mobileDots: 'under',
            colorScheme: 'light',
            paddingTop: 36,
            paddingBottom: 36,
            mobilePaddingTop: 27,
            mobilePaddingBottom: 27,
            width: 'standard',
        },
        // Three default slides — each an empty `column` block, so the placeholder SVG fills each until the seller
        // pastes a TikTok embed code (exactly Shrine's default preset: three empty `column` blocks).
        blocks: [{ type: 'column' }, { type: 'column' }, { type: 'column' }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const tiktokVideosMeta = {
    label: 'TikTok videos',
    group: 'Content',
    icon: 'video',
};
//# sourceMappingURL=schema.js.map